// Push the outbox, pull everything since our cursor. Runs on: start, coming online,
// app becoming visible, every 5 minutes while visible, and shortly after a change.
// Never blocks the UI; failures just leave events queued.
import { create } from 'zustand'
import { MAX_EVENTS_PER_PUSH, type SyncResponse } from '../../shared/events'
import { api, HttpError, Offline } from './api'
import { db, kvGet, kvSet, type StoredEvent } from './db'
import { onRecorded } from './events'

export type SyncState = 'synced' | 'queued' | 'offline' | 'syncing'

interface SyncStatus {
  state: SyncState
  queued: number
  lastSyncAt: number | null
  offlineSince: number | null
}

export const useSync = create<SyncStatus>(() => ({ state: 'synced', queued: 0, lastSyncAt: null, offlineSince: null }))

async function refreshQueued() {
  const queued = await db.outbox.count()
  useSync.setState((s) => ({
    queued,
    state: s.state === 'offline' || s.state === 'syncing' ? s.state : queued ? 'queued' : 'synced',
  }))
}

export interface SyncDeps {
  /** Called on 401; should re-issue the session (true if it worked). */
  renew(): Promise<boolean>
  /** Who's signed in: only their queued events are pushed (the server stamps the session's person). */
  personId?(): string | undefined
  now?(): number
}

let running: Promise<void> | null = null

/** One sync request gives up after this long (it's then retried at the next trigger). */
const SYNC_TIMEOUT_MS = 30_000
const timeout = (ms: number): AbortSignal | undefined => (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal ? AbortSignal.timeout(ms) : undefined)

/** One full sync pass (push all, pull all). Concurrent calls share the same pass. */
export function syncNow(deps: SyncDeps): Promise<void> {
  running ??= pass(deps).finally(() => {
    running = null
  })
  return running
}

async function pass(deps: SyncDeps, retried = false): Promise<void> {
  const now = deps.now ?? Date.now
  useSync.setState({ state: 'syncing' })
  try {
    for (;;) {
      const since = (await kvGet<number>('syncCursor')) ?? 0
      const me = deps.personId?.()
      // Someone else's queued events (made before a switch on this phone) wait for their own session.
      const mine = (e: { personId?: string }) => !me || !e.personId || e.personId === me
      const batch = await db.outbox.orderBy('at').filter(mine).limit(MAX_EVENTS_PER_PUSH).toArray()
      const events = batch.map(({ personId: _who, ...e }) => e)
      // A request that never answers (the radio changed while the app slept) must not hold the
      // shared pass forever: every later sync would wait on it (WI-40 #8).
      const res = await api<SyncResponse>('/api/sync', { method: 'POST', body: JSON.stringify({ since, events }), signal: timeout(SYNC_TIMEOUT_MS) })
      await db.transaction('rw', db.outbox, db.events, db.kv, async () => {
        const rejected = res.rejected.map((r) => r.id)
        await db.outbox.bulkDelete([...res.accepted, ...rejected])
        // The server refused these (e.g. a child granting plays), so they stop counting here too.
        if (rejected.length) await db.events.bulkDelete(rejected)
        await db.events.bulkPut(res.events.map((e): StoredEvent => e))
        await kvSet('syncCursor', res.cursor)
      })
      const left = await db.outbox.filter(mine).count()
      if (!res.more && (left === 0 || batch.length === 0)) break
    }
    const t = now()
    await kvSet('lastSyncAt', t)
    useSync.setState({ state: 'synced', lastSyncAt: t, offlineSince: null })
  } catch (e) {
    if (e instanceof HttpError && e.status === 401 && !retried && (await deps.renew())) return pass(deps, true)
    if (e instanceof Offline) {
      useSync.setState((s) => ({ state: 'offline', offlineSince: s.offlineSince ?? now() }))
    } else {
      useSync.setState({ state: 'queued' })
      console.warn('sync failed', e)
    }
  } finally {
    if (useSync.getState().state === 'syncing') useSync.setState({ state: 'synced' })
    await refreshQueued()
  }
}

/**
 * B19: events queued by an app version from before queued events remembered their person go out
 * as whoever was signed in when this version first starts, not as whoever syncs next.
 */
export async function stampUnownedOutbox(personId: string): Promise<number> {
  return db.outbox.filter((e) => !e.personId).modify({ personId })
}

const FIVE_MIN = 5 * 60 * 1000
const RECENT_MS = 30_000

/** Wires sync to the browser. Returns a stop function. */
export function startSync(deps: SyncDeps): () => void {
  let debounce: ReturnType<typeof setTimeout> | undefined
  const run = () => void syncNow(deps)
  const soon = () => {
    void refreshQueued()
    clearTimeout(debounce)
    debounce = setTimeout(run, 1500)
  }
  const onVisible = () => document.visibilityState === 'visible' && run()
  // WI-40 #8: an installed app brought back from the background doesn't always fire
  // visibilitychange (a restored page, a window focused again): those sync too, unless one ran
  // in the last half minute.
  const onBack = () => {
    const last = useSync.getState().lastSyncAt
    if (document.visibilityState === 'visible' && (!last || Date.now() - last > RECENT_MS)) run()
  }
  const onOffline = () => useSync.setState((s) => ({ state: 'offline', offlineSince: s.offlineSince ?? Date.now() }))

  void kvGet<number>('lastSyncAt').then((t) => t && useSync.setState((s) => ({ lastSyncAt: s.lastSyncAt ?? t })))
  const unsub = onRecorded(soon)
  window.addEventListener('online', run)
  window.addEventListener('offline', onOffline)
  window.addEventListener('pageshow', onBack)
  window.addEventListener('focus', onBack)
  document.addEventListener('visibilitychange', onVisible)
  const timer = setInterval(() => document.visibilityState === 'visible' && run(), FIVE_MIN)
  if (navigator.onLine) run()
  else onOffline()

  return () => {
    unsub()
    clearTimeout(debounce)
    clearInterval(timer)
    window.removeEventListener('online', run)
    window.removeEventListener('offline', onOffline)
    window.removeEventListener('pageshow', onBack)
    window.removeEventListener('focus', onBack)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
